import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Plus, Pencil, Trash2, X,
  BookOpen, Search, Check, Tag, ToggleLeft, ToggleRight,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const INTENT_OPTIONS = [
  'contract', 'safety', 'technical_reference', 'job_briefing',
  'fact_finder', 'management_communication', 'grievance_prep',
  'statement_builder', 'leave_policy', 'discipline', 'overtime',
  'general_union_help',
];

const INTENT_COLORS = {
  contract: '#3b82f6',
  safety: '#ef4444',
  technical_reference: '#8b5cf6',
  job_briefing: '#06b6d4',
  fact_finder: '#f97316',
  management_communication: '#ec4899',
  grievance_prep: '#eab308',
  statement_builder: '#14b8a6',
  leave_policy: '#10b981',
  discipline: '#f43f5e',
  overtime: '#a855f7',
  general_union_help: '#64748b',
};

const EMPTY_FORM = {
  canonical_term: '',
  synonyms: '',
  intent: 'general_union_help',
  related_terms: '',
  source_category: '',
  priority: 50,
  notes: '',
  is_active: true,
};

export default function VocabularyAdminPage({ user }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/vocabulary/`, { headers });
      setEntries(res.data);
    } catch (err) {
      console.error('Failed to fetch vocabulary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openCreate = () => {
    setEditingTerm(null);
    setForm({ ...EMPTY_FORM });
    setError('');
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setEditingTerm(entry.canonical_term);
    setForm({
      canonical_term: entry.canonical_term,
      synonyms: (entry.synonyms || []).join(', '),
      intent: entry.intent,
      related_terms: (entry.related_terms || []).join(', '),
      source_category: entry.source_category || '',
      priority: entry.priority,
      notes: entry.notes || '',
      is_active: entry.is_active,
    });
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTerm(null);
    setError('');
  };

  const parseCSV = (str) =>
    str.split(',').map(s => s.trim()).filter(Boolean);

  const handleSave = async () => {
    if (!form.canonical_term.trim()) {
      setError('Canonical term is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingTerm) {
        await axios.put(`${API}/api/vocabulary/${encodeURIComponent(editingTerm)}`, {
          synonyms: parseCSV(form.synonyms),
          related_terms: parseCSV(form.related_terms),
          intent: form.intent,
          source_category: form.source_category || null,
          priority: Number(form.priority),
          notes: form.notes || null,
          is_active: form.is_active,
        }, { headers });
      } else {
        await axios.post(`${API}/api/vocabulary/`, {
          canonical_term: form.canonical_term.trim(),
          synonyms: parseCSV(form.synonyms),
          related_terms: parseCSV(form.related_terms),
          intent: form.intent,
          source_category: form.source_category || null,
          priority: Number(form.priority),
          notes: form.notes || null,
        }, { headers });
      }
      closeForm();
      fetchEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (canonicalTerm) => {
    if (!window.confirm(`Delete vocabulary entry "${canonicalTerm}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/vocabulary/${encodeURIComponent(canonicalTerm)}`, { headers });
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete entry.');
    }
  };

  const filtered = entries.filter(e => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      e.canonical_term.toLowerCase().includes(q) ||
      (e.synonyms || []).some(s => s.toLowerCase().includes(q)) ||
      e.intent.toLowerCase().includes(q)
    );
  });

  const activeCount = entries.filter(e => e.is_active).length;

  return (
    <div className="va-dash" data-testid="vocabulary-admin-page">
      <style>{vaStyles}</style>

      {/* Header */}
      <div className="va-header">
        <div className="va-header-left">
          <div>
            <h1 className="va-title">Union Vocabulary Trainer</h1>
            <p className="va-subtitle">
              Manage term mappings to improve semantic search ({activeCount} active of {entries.length} total)
            </p>
          </div>
        </div>
        <div className="va-header-right">
          <button className="va-btn-primary" onClick={openCreate} data-testid="va-add-btn">
            <Plus size={14} /> Add Term
          </button>
          <button className="va-refresh" onClick={fetchEntries} disabled={loading} data-testid="va-refresh-btn">
            <RefreshCw size={14} className={loading ? 'va-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="va-search-bar" data-testid="va-search-bar">
        <Search size={16} className="va-search-icon" />
        <input
          className="va-search-input"
          placeholder="Search terms, synonyms, or intent..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          data-testid="va-search-input"
        />
        {searchTerm && (
          <button className="va-search-clear" onClick={() => setSearchTerm('')}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="va-loading">
          <RefreshCw size={24} className="va-spin" />
          <span>Loading vocabulary...</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && entries.length === 0 && (
        <div className="va-empty">
          <BookOpen size={48} />
          <h3>No vocabulary entries yet</h3>
          <p>Add terms to help Stewie understand everyday language and map it to formal contract terminology.</p>
          <button className="va-btn-primary" onClick={openCreate}>
            <Plus size={14} /> Add First Term
          </button>
        </div>
      )}

      {/* Entries table */}
      {!loading && filtered.length > 0 && (
        <div className="va-table-wrap" data-testid="va-entries-table">
          <table className="va-table">
            <thead>
              <tr>
                <th>Canonical Term</th>
                <th>Synonyms</th>
                <th>Intent</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => (
                <tr key={entry.canonical_term} className={!entry.is_active ? 'va-row-inactive' : ''} data-testid={`va-row-${entry.canonical_term}`}>
                  <td className="va-cell-term">
                    <span className="va-term-name">{entry.canonical_term}</span>
                    {entry.source_category && (
                      <span className="va-source-badge">{entry.source_category}</span>
                    )}
                    {entry.notes && <span className="va-notes-hint" title={entry.notes}>note</span>}
                  </td>
                  <td className="va-cell-synonyms">
                    <div className="va-pills">
                      {(entry.synonyms || []).slice(0, 5).map((s, i) => (
                        <span key={i} className="va-pill">{s}</span>
                      ))}
                      {(entry.synonyms || []).length > 5 && (
                        <span className="va-pill va-pill-more">+{entry.synonyms.length - 5}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className="va-intent-badge"
                      style={{
                        color: INTENT_COLORS[entry.intent] || '#64748b',
                        background: `${INTENT_COLORS[entry.intent] || '#64748b'}18`,
                      }}
                    >
                      {entry.intent.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>
                    <div className="va-priority-bar-wrap">
                      <div
                        className="va-priority-bar"
                        style={{ width: `${entry.priority}%`, background: entry.priority >= 80 ? '#10b981' : entry.priority >= 50 ? '#f59e0b' : '#64748b' }}
                      />
                    </div>
                    <span className="va-priority-num">{entry.priority}</span>
                  </td>
                  <td>
                    {entry.is_active ? (
                      <span className="va-status-active" data-testid={`va-status-${entry.canonical_term}`}>
                        <ToggleRight size={14} /> Active
                      </span>
                    ) : (
                      <span className="va-status-inactive" data-testid={`va-status-${entry.canonical_term}`}>
                        <ToggleLeft size={14} /> Inactive
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="va-actions">
                      <button className="va-action-btn va-action-edit" onClick={() => openEdit(entry)} data-testid={`va-edit-${entry.canonical_term}`}>
                        <Pencil size={13} />
                      </button>
                      <button className="va-action-btn va-action-delete" onClick={() => handleDelete(entry.canonical_term)} data-testid={`va-delete-${entry.canonical_term}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && searchTerm && filtered.length === 0 && entries.length > 0 && (
        <div className="va-no-results">
          No entries match "{searchTerm}"
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="va-overlay" onClick={closeForm}>
          <div className="va-modal" onClick={e => e.stopPropagation()} data-testid="va-form-modal">
            <div className="va-modal-header">
              <h3>{editingTerm ? `Edit: ${editingTerm}` : 'Add New Vocabulary Entry'}</h3>
              <button className="va-close" onClick={closeForm}><X size={18} /></button>
            </div>
            <div className="va-modal-body">
              {error && <div className="va-form-error" data-testid="va-form-error">{error}</div>}

              <div className="va-field">
                <label>Canonical Term *</label>
                <input
                  value={form.canonical_term}
                  onChange={e => setForm({ ...form, canonical_term: e.target.value })}
                  placeholder="e.g. bereavement"
                  disabled={!!editingTerm}
                  data-testid="va-input-canonical"
                />
              </div>

              <div className="va-field">
                <label>Synonyms (comma-separated)</label>
                <textarea
                  value={form.synonyms}
                  onChange={e => setForm({ ...form, synonyms: e.target.value })}
                  placeholder="death in family, funeral leave, time off for funeral"
                  rows={3}
                  data-testid="va-input-synonyms"
                />
              </div>

              <div className="va-field-row">
                <div className="va-field">
                  <label>Intent</label>
                  <select
                    value={form.intent}
                    onChange={e => setForm({ ...form, intent: e.target.value })}
                    data-testid="va-input-intent"
                  >
                    {INTENT_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="va-field">
                  <label>Priority (0–100)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.priority}
                    onChange={e => setForm({ ...form, priority: e.target.value })}
                    data-testid="va-input-priority"
                  />
                </div>
              </div>

              <div className="va-field">
                <label>Related Terms (comma-separated)</label>
                <textarea
                  value={form.related_terms}
                  onChange={e => setForm({ ...form, related_terms: e.target.value })}
                  placeholder="bereavement leave, leave policy, time off"
                  rows={2}
                  data-testid="va-input-related"
                />
              </div>

              <div className="va-field-row">
                <div className="va-field">
                  <label>Source Category</label>
                  <input
                    value={form.source_category}
                    onChange={e => setForm({ ...form, source_category: e.target.value })}
                    placeholder="e.g. union_contract"
                    data-testid="va-input-source"
                  />
                </div>
                {editingTerm && (
                  <div className="va-field">
                    <label>Active</label>
                    <button
                      type="button"
                      className={`va-toggle-btn ${form.is_active ? 'va-toggle-on' : 'va-toggle-off'}`}
                      onClick={() => setForm({ ...form, is_active: !form.is_active })}
                      data-testid="va-toggle-active"
                    >
                      {form.is_active ? <><ToggleRight size={16} /> Active</> : <><ToggleLeft size={16} /> Inactive</>}
                    </button>
                  </div>
                )}
              </div>

              <div className="va-field">
                <label>Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional admin notes about this mapping"
                  rows={2}
                  data-testid="va-input-notes"
                />
              </div>
            </div>
            <div className="va-modal-footer">
              <button className="va-btn-cancel" onClick={closeForm}>Cancel</button>
              <button className="va-btn-primary" onClick={handleSave} disabled={saving} data-testid="va-save-btn">
                {saving ? 'Saving...' : <><Check size={14} /> {editingTerm ? 'Update' : 'Create'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const vaStyles = `
  .va-dash {
    min-height: 100vh;
    background: #0b1120;
    color: #e2e8f0;
    padding: 24px 32px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .va-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 20px; flex-wrap: wrap; gap: 16px;
  }
  .va-header-left { display: flex; align-items: center; gap: 12px; }
  .va-header-right { display: flex; align-items: center; gap: 10px; }
  .va-back {
    display: flex; align-items: center; gap: 6px;
    background: transparent; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
    transition: all 0.15s;
  }
  .va-back:hover { color: #e2e8f0; border-color: #475569; }
  .va-title { font-size: 22px; font-weight: 700; color: #f8fafc; margin: 0; }
  .va-subtitle { font-size: 13px; color: #64748b; margin: 2px 0 0; }

  .va-btn-primary {
    display: flex; align-items: center; gap: 6px;
    background: #3b82f6; color: white; border: none; padding: 8px 16px;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600;
    transition: background 0.15s;
  }
  .va-btn-primary:hover { background: #2563eb; }
  .va-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .va-btn-cancel {
    background: transparent; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 16px; border-radius: 8px; cursor: pointer; font-size: 13px;
  }
  .va-btn-cancel:hover { color: #e2e8f0; border-color: #475569; }

  .va-refresh {
    display: flex; align-items: center; gap: 6px;
    background: transparent; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
  }
  .va-refresh:hover { color: #e2e8f0; border-color: #475569; }
  .va-refresh:disabled { opacity: 0.6; }
  .va-spin { animation: va-spin-anim 1s linear infinite; }
  @keyframes va-spin-anim { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* Search */
  .va-search-bar {
    position: relative; margin-bottom: 20px;
  }
  .va-search-icon {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #475569;
  }
  .va-search-input {
    width: 100%; background: #111827; border: 1px solid #1e293b; border-radius: 10px;
    padding: 10px 40px 10px 40px; color: #e2e8f0; font-size: 14px; outline: none;
    box-sizing: border-box;
  }
  .va-search-input:focus { border-color: #3b82f6; }
  .va-search-input::placeholder { color: #475569; }
  .va-search-clear {
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    background: none; border: none; color: #64748b; cursor: pointer;
  }
  .va-search-clear:hover { color: #e2e8f0; }

  /* Loading & Empty */
  .va-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 300px; color: #64748b; gap: 12px;
  }
  .va-empty {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 400px; color: #64748b; gap: 14px; text-align: center;
  }
  .va-empty h3 { color: #94a3b8; margin: 0; font-size: 18px; }
  .va-empty p { max-width: 420px; font-size: 14px; line-height: 1.6; }
  .va-no-results { text-align: center; color: #475569; padding: 40px; font-size: 14px; }

  /* Table */
  .va-table-wrap {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px;
    overflow: hidden;
  }
  .va-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
  }
  .va-table thead th {
    text-align: left; padding: 12px 16px; font-size: 11px; font-weight: 600;
    color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;
    border-bottom: 1px solid #1e293b; background: #0f172a;
  }
  .va-table tbody tr {
    border-bottom: 1px solid #1e293b; transition: background 0.1s;
  }
  .va-table tbody tr:hover { background: #1e293b40; }
  .va-table tbody tr:last-child { border-bottom: none; }
  .va-table td { padding: 12px 16px; vertical-align: middle; }
  .va-row-inactive { opacity: 0.5; }

  .va-cell-term { min-width: 140px; }
  .va-term-name { font-weight: 600; color: #f8fafc; display: block; margin-bottom: 2px; text-transform: capitalize; }
  .va-source-badge {
    display: inline-block; font-size: 10px; color: #64748b; background: #1e293b;
    padding: 1px 6px; border-radius: 4px; margin-right: 4px;
  }
  .va-notes-hint {
    display: inline-block; font-size: 10px; color: #3b82f6; background: #3b82f618;
    padding: 1px 6px; border-radius: 4px; cursor: help;
  }

  .va-cell-synonyms { max-width: 340px; }
  .va-pills { display: flex; flex-wrap: wrap; gap: 4px; }
  .va-pill {
    background: #1e293b; color: #94a3b8; padding: 2px 8px; border-radius: 5px;
    font-size: 11px; white-space: nowrap;
  }
  .va-pill-more { color: #3b82f6; background: #3b82f618; font-weight: 600; }

  .va-intent-badge {
    display: inline-block; padding: 3px 10px; border-radius: 6px;
    font-size: 11px; font-weight: 600; text-transform: capitalize;
  }

  .va-priority-bar-wrap {
    width: 60px; height: 5px; background: #1e293b; border-radius: 3px;
    display: inline-block; vertical-align: middle; margin-right: 6px;
  }
  .va-priority-bar { height: 100%; border-radius: 3px; transition: width 0.3s; }
  .va-priority-num { font-size: 12px; color: #94a3b8; font-weight: 600; }

  .va-status-active { display: flex; align-items: center; gap: 4px; color: #10b981; font-size: 12px; font-weight: 600; }
  .va-status-inactive { display: flex; align-items: center; gap: 4px; color: #64748b; font-size: 12px; font-weight: 600; }

  .va-actions { display: flex; gap: 4px; }
  .va-action-btn {
    width: 30px; height: 30px; border-radius: 7px; border: 1px solid #1e293b;
    background: transparent; cursor: pointer; display: flex; align-items: center;
    justify-content: center; transition: all 0.15s;
  }
  .va-action-edit { color: #3b82f6; }
  .va-action-edit:hover { background: #3b82f618; border-color: #3b82f650; }
  .va-action-delete { color: #ef4444; }
  .va-action-delete:hover { background: #ef444418; border-color: #ef444450; }

  /* Modal */
  .va-overlay {
    position: fixed; inset: 0; background: #00000080; z-index: 100;
    display: flex; align-items: center; justify-content: center;
  }
  .va-modal {
    background: #111827; border: 1px solid #1e293b; border-radius: 14px;
    width: 560px; max-width: 95vw; max-height: 90vh; overflow-y: auto;
  }
  .va-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 22px; border-bottom: 1px solid #1e293b;
  }
  .va-modal-header h3 { margin: 0; font-size: 16px; color: #f8fafc; }
  .va-close { background: transparent; border: none; color: #64748b; cursor: pointer; }
  .va-close:hover { color: #e2e8f0; }
  .va-modal-body { padding: 22px; }
  .va-modal-footer {
    display: flex; justify-content: flex-end; gap: 8px;
    padding: 14px 22px; border-top: 1px solid #1e293b;
  }

  .va-form-error {
    background: #ef444418; border: 1px solid #ef444440; color: #fca5a5;
    padding: 8px 12px; border-radius: 8px; font-size: 13px; margin-bottom: 14px;
  }

  .va-field { margin-bottom: 14px; }
  .va-field label {
    display: block; font-size: 11px; font-weight: 600; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 5px;
  }
  .va-field input, .va-field select, .va-field textarea {
    width: 100%; background: #0f172a; border: 1px solid #1e293b; border-radius: 8px;
    padding: 9px 12px; color: #e2e8f0; font-size: 13px; outline: none;
    font-family: inherit; box-sizing: border-box;
  }
  .va-field input:focus, .va-field select:focus, .va-field textarea:focus { border-color: #3b82f6; }
  .va-field input::placeholder, .va-field textarea::placeholder { color: #475569; }
  .va-field input:disabled { opacity: 0.5; cursor: not-allowed; }
  .va-field select option { background: #1e293b; }
  .va-field textarea { resize: vertical; }

  .va-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  .va-toggle-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 9px 14px; border-radius: 8px; border: 1px solid #1e293b;
    cursor: pointer; font-size: 13px; font-weight: 600; width: 100%;
    box-sizing: border-box;
  }
  .va-toggle-on { background: #10b98118; color: #10b981; border-color: #10b98140; }
  .va-toggle-off { background: #64748b18; color: #64748b; border-color: #64748b40; }

  @media (max-width: 768px) {
    .va-dash { padding: 16px; }
    .va-header { flex-direction: column; }
    .va-field-row { grid-template-columns: 1fr; }
    .va-table-wrap { overflow-x: auto; }
    .va-table { min-width: 700px; }
  }
`;
